import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Note } from '../types';
import { Plus, StickyNote } from 'lucide-react';
import NoteList from './notes/NoteList';
import NoteEditor from './notes/NoteEditor';

interface NotesSidebarProps {
  selectedChapterId?: string | null;
  userId: string;
}

export default function NotesSidebar({ selectedChapterId, userId }: NotesSidebarProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [selectedNote, setSelectedNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedChapterId) {
      setNotes([]);
      setSelectedNote(null);
      return;
    }

    const loadNotes = async () => {
      try {
        const { data, error } = await supabase
          .from('notes')
          .select('*')
          .eq('chapter_id', selectedChapterId)
          .order('position', { ascending: true });

        if (error) throw error;
        setNotes(data);
      } catch (error) {
        console.error('Error loading notes:', error);
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, [selectedChapterId]);

  const createNote = async () => {
    if (!selectedChapterId) return;

    try {
      // Get the highest position
      const maxPosition = Math.max(...notes.map(n => n.position || 0), -1);

      const { data, error } = await supabase
        .from('notes')
        .insert({
          chapter_id: selectedChapterId,
          user_id: userId,
          content: '<h2>New Note</h2><p>Start writing your note here...</p>',
          position: maxPosition + 1
        })
        .select()
        .single();

      if (error) throw error;

      setNotes(prev => [data, ...prev]);
      setSelectedNote(data.id);
    } catch (error) {
      console.error('Error creating note:', error);
    }
  };

  const updateNote = async (noteId: string, content: string) => {
    try {
      const { error } = await supabase
        .from('notes')
        .update({
          content,
          updated_at: new Date().toISOString()
        })
        .eq('id', noteId);

      if (error) throw error;

      setNotes(prev =>
        prev.map(note =>
          note.id === noteId
            ? { ...note, content, updated_at: new Date().toISOString() }
            : note
        )
      );
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const deleteNote = async (noteId: string) => {
    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      setNotes(prev => prev.filter(note => note.id !== noteId));
      if (selectedNote === noteId) {
        setSelectedNote(null);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const reorderNotes = async (reorderedNotes: Note[]) => {
    try {
      // Update positions in the database
      const updates = reorderedNotes.map((note, index) => ({
        id: note.id,
        position: index
      }));

      const { error } = await supabase
        .from('notes')
        .upsert(updates, { onConflict: 'id' });

      if (error) throw error;

      // Update local state
      setNotes(reorderedNotes);
    } catch (error) {
      console.error('Error reordering notes:', error);
    }
  };

  const selectedNoteData = notes.find(note => note.id === selectedNote);

  return (
    <aside className="w-64 lg:w-72 xl:w-80 bg-gray-50 border-l border-gray-200 flex flex-col shrink-0">
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <StickyNote className="w-5 h-5" />
            Notes
          </h2>
          {selectedChapterId && (
            <button
              onClick={createNote}
              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
              title="Add note"
            >
              <Plus className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {selectedNote && selectedNoteData ? (
          <NoteEditor
            note={selectedNoteData}
            onClose={() => setSelectedNote(null)}
            onUpdate={updateNote}
            onDelete={deleteNote}
          />
        ) : (
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-y-auto">
              {selectedChapterId ? (
                notes.length > 0 ? (
                  <NoteList
                    notes={notes}
                    onSelectNote={setSelectedNote}
                    onDeleteNote={deleteNote}
                    onReorder={reorderNotes}
                  />
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <StickyNote className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-sm">No notes yet</p>
                    <button
                      onClick={createNote}
                      className="mt-2 text-indigo-600 hover:text-indigo-800"
                    >
                      Create your first note
                    </button>
                  </div>
                )
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <StickyNote className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm">Select a chapter to view notes</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}